const MEDIA_HANDOFF_TTL_MS = 15000;

let pendingStream: MediaStream | null = null;
let expiryTimer: ReturnType<typeof setTimeout> | null = null;

export type InterviewMediaHandoffStatus = 'available' | 'missing' | 'invalid';
export type InterviewMediaAttachmentStatus = 'attached' | 'missing_element' | 'invalid_stream' | 'playback_failed';
export type InterviewMediaAcquisitionOutcome =
  | 'ready'
  | 'reused_preview_stream'
  | 'handoff_missing_reacquired'
  | 'handoff_invalid_reacquired'
  | 'invalid_reacquired_stream';

export const stopInterviewMediaStream = (stream: MediaStream | null) => {
  stream?.getTracks().forEach((track) => {
    track.onended = null;
    track.onmute = null;
    track.onunmute = null;
    try { track.stop(); } catch (_) {}
  });
};

const clearExpiryTimer = () => {
  if (expiryTimer !== null) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
  }
};

export const isInterviewMediaStreamUsable = (stream: MediaStream | null) => Boolean(
  stream
  && stream.getVideoTracks().some((track) => track.readyState === 'live' && track.enabled && !track.muted)
  && stream.getAudioTracks().some((track) => track.readyState === 'live' && track.enabled && !track.muted)
);

export const stageInterviewMediaHandoff = (stream: MediaStream | null): boolean => {
  if (!isInterviewMediaStreamUsable(stream)) return false;

  clearExpiryTimer();
  if (pendingStream && pendingStream !== stream) stopInterviewMediaStream(pendingStream);

  pendingStream = stream;
  stream!.getTracks().forEach((track) => {
    track.onended = null;
    track.onmute = null;
    track.onunmute = null;
  });
  expiryTimer = setTimeout(() => {
    const expiredStream = pendingStream;
    pendingStream = null;
    expiryTimer = null;
    stopInterviewMediaStream(expiredStream);
  }, MEDIA_HANDOFF_TTL_MS);
  return true;
};

export const consumeInterviewMediaHandoff = (): {
  stream: MediaStream | null;
  status: InterviewMediaHandoffStatus;
} => {
  clearExpiryTimer();
  const stream = pendingStream;
  pendingStream = null;
  if (!stream) {
    return { stream: null, status: 'missing' };
  }
  if (!isInterviewMediaStreamUsable(stream)) {
    stopInterviewMediaStream(stream);
    return { stream: null, status: 'invalid' };
  }
  return { stream, status: 'available' };
};

export const takeInterviewMediaHandoff = (): MediaStream | null => {
  return consumeInterviewMediaHandoff().stream;
};

export const attachInterviewMediaStream = async (
  videoElement: HTMLVideoElement | null,
  stream: MediaStream | null,
): Promise<InterviewMediaAttachmentStatus> => {
  if (!videoElement) return 'missing_element';
  if (!isInterviewMediaStreamUsable(stream)) return 'invalid_stream';

  videoElement.autoplay = true;
  videoElement.muted = true;
  videoElement.playsInline = true;
  videoElement.srcObject = stream;

  try {
    await videoElement.play();
  } catch (_) {
    if (videoElement.srcObject === stream) videoElement.srcObject = null;
    return 'playback_failed';
  }

  if (!isInterviewMediaStreamUsable(stream)) {
    if (videoElement.srcObject === stream) videoElement.srcObject = null;
    return 'invalid_stream';
  }

  return 'attached';
};

export const acquireInterviewMediaStream = async ({
  reusePreparedMedia,
  reacquire,
}: {
  reusePreparedMedia: boolean;
  reacquire: () => Promise<MediaStream>;
}): Promise<{
  stream: MediaStream | null;
  outcome: InterviewMediaAcquisitionOutcome;
  reusedHandoff: boolean;
}> => {
  const handoff = reusePreparedMedia
    ? consumeInterviewMediaHandoff()
    : { stream: null, status: 'missing' as const };

  if (handoff.stream) {
    return {
      stream: handoff.stream,
      outcome: 'reused_preview_stream',
      reusedHandoff: true,
    };
  }

  const reacquiredStream = await reacquire();
  if (!isInterviewMediaStreamUsable(reacquiredStream)) {
    stopInterviewMediaStream(reacquiredStream);
    return {
      stream: null,
      outcome: 'invalid_reacquired_stream',
      reusedHandoff: false,
    };
  }

  return {
    stream: reacquiredStream,
    outcome: reusePreparedMedia
      ? handoff.status === 'invalid'
        ? 'handoff_invalid_reacquired'
        : 'handoff_missing_reacquired'
      : 'ready',
    reusedHandoff: false,
  };
};

export const discardInterviewMediaHandoff = () => {
  clearExpiryTimer();
  const stream = pendingStream;
  pendingStream = null;
  stopInterviewMediaStream(stream);
};
